import { useEffect, useMemo, useRef, useState } from "react";

import {
  addTaskRecipient,
  fetchOncoEmrUserDirectory,
  retryTaskWriteback,
} from "../../../api/tasks/tasksApi";
import { OncoEMRUserSearchResult } from "../../../api/tasks/taskTypes";

interface RecipientPickerProps {
  taskRowId: string;
  orgId: string;
}

type AddState = "idle" | "sending" | "sent" | "failed";

/** Max name matches to show in the typeahead dropdown at once. */
const MAX_SUGGESTIONS = 8;

/**
 * Resolve the recipient id + display name to add from the current input.
 *
 * Prefers an explicit dropdown pick; otherwise matches the typed text against
 * the directory by exact name or id (case-insensitive), and finally falls back
 * to sending the raw text as the OncoEMR UserId (an un-synced user is still
 * addable — OncoEMR validates the id, the directory only powers name display).
 */
function resolveRecipient(
  query: string,
  picked: OncoEMRUserSearchResult | null,
  directory: OncoEMRUserSearchResult[],
): { id: string; label: string } | null {
  const text = query.trim();
  if (picked && picked.first_and_last === query) {
    return { id: picked.oncoemr_user_id, label: query };
  }
  if (!text) return null;
  const lower = text.toLowerCase();
  const match = directory.find(
    (u) =>
      (u.first_and_last ?? "").toLowerCase() === lower ||
      u.oncoemr_user_id.toLowerCase() === lower,
  );
  if (match) {
    return {
      id: match.oncoemr_user_id,
      label: match.first_and_last ?? match.oncoemr_user_id,
    };
  }
  return { id: text, label: text };
}

/**
 * Add-recipient control: a name typeahead over the cached OncoEMR user
 * directory that resolves the picked name to its UserId, with a raw-UserId
 * fallback. Mount with `key={taskRowId}` so it resets per task.
 */
const RecipientPicker = ({ taskRowId, orgId }: RecipientPickerProps) => {
  const [directory, setDirectory] = useState<OncoEMRUserSearchResult[]>([]);
  const [dirState, setDirState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<OncoEMRUserSearchResult | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addState, setAddState] = useState<AddState>("idle");
  const [addedLabel, setAddedLabel] = useState("");
  const addKey = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setDirState("loading");
    fetchOncoEmrUserDirectory(orgId)
      .then((users) => {
        if (!active) return;
        setDirectory(users);
        setDirState("ready");
      })
      .catch(() => {
        // Directory is a convenience layer only — raw-UserId entry still works.
        if (active) setDirState("error");
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const suggestions = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return [];
    return directory
      .filter((u) => (u.first_and_last ?? "").toLowerCase().includes(text))
      .slice(0, MAX_SUGGESTIONS);
  }, [query, directory]);

  const runAdd = async (send: () => ReturnType<typeof addTaskRecipient>) => {
    setAddState("sending");
    try {
      const result = await send();
      setAddState(result.status === "sent" ? "sent" : "failed");
      if (result.status === "sent") {
        setQuery("");
        setPicked(null);
      }
    } catch {
      setAddState("failed");
    }
  };

  const handleAdd = () => {
    if (addState === "sending") return;
    
    if (!picked && suggestions.length > 0) {
      handleSelect(suggestions[0]);
      return;
    }
    const resolved = resolveRecipient(query, picked, directory);
    if (!resolved) return;
    const key = crypto.randomUUID();
    addKey.current = key;
    setAddedLabel(resolved.label);
    setShowSuggestions(false);
    void runAdd(() => addTaskRecipient(taskRowId, resolved.id, key));
  };

  const handleRetry = () => {
    if (!addKey.current) return;
    void runAdd(() => retryTaskWriteback(addKey.current as string));
  };

  const handleSelect = (user: OncoEMRUserSearchResult) => {
    setPicked(user);
    setQuery(user.first_and_last ?? user.oncoemr_user_id);
    setShowSuggestions(false);
    setAddState("idle");
  };

  const hasQuery = query.trim() !== "";
  let directoryHint = "";
  if (dirState === "loading") {
    directoryHint = "Loading the OncoEMR user directory…";
  } else if (dirState === "error") {
    directoryHint =
      "Couldn't load the name directory — you can still paste a UserId to add.";
  } else if (directory.length === 0) {
    directoryHint =
      "Name directory is empty (not synced yet) — paste a UserId to add.";
  } else if (hasQuery && suggestions.length === 0 && !picked) {
    directoryHint = "No matching name — “Add” will use exactly what you typed.";
  }

  return (
    <div className="mt-5 flex flex-col gap-1 border-t border-primaryGray-15 pt-4">
      <span className="text-tiny text-primaryGray-7">Add recipient</span>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            disabled={addState === "sending"}
            placeholder="Search by name, or paste a UserId"
            className="w-full rounded border border-primaryGray-14 px-3 py-2 text-small text-primaryGray-2 focus:border-tertiaryBlue-5 focus:outline-none disabled:bg-primaryGray-16"
            onChange={(e) => {
              setQuery(e.target.value);
              setPicked(null);
              setShowSuggestions(true);
              if (addState !== "sending") setAddState("idle");
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                // With the dropdown open, Enter picks the top match instead of
                // POSTing the raw typed text as a bogus UserId.
                if (showSuggestions && suggestions.length > 0) {
                  handleSelect(suggestions[0]);
                  return;
                }
                handleAdd();
              }
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded border border-primaryGray-14 bg-white shadow-lg">
              {suggestions.map((user) => (
                <li key={user.oncoemr_user_id}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-primaryGray-16"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(user)}
                  >
                    <span className="text-small text-primaryGray-2">
                      {user.first_and_last ?? user.oncoemr_user_id}
                    </span>
                    <span className="text-tiny text-primaryGray-7">
                      {user.oncoemr_user_id}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          disabled={addState === "sending" || query.trim() === ""}
          className="rounded bg-tertiaryBlue-5 px-4 py-2 text-small font-semiBold text-white disabled:cursor-not-allowed disabled:bg-primaryGray-14 disabled:text-primaryGray-9"
          onClick={handleAdd}
        >
          {addState === "sending" ? "Adding…" : "Add"}
        </button>
      </div>
      {addState !== "failed" && directoryHint !== "" && (
        <span className="text-tiny text-primaryGray-7">{directoryHint}</span>
      )}
      {addState === "sent" && (
        <span className="text-tiny font-semiBold" style={{ color: "#005D49" }}>
          Added {addedLabel} ✓
        </span>
      )}
      {addState === "failed" && (
        <div className="flex items-center gap-2 text-tiny text-tertiaryRed-3">
          <span>Failed to add recipient.</span>
          <button
            type="button"
            className="font-semiBold hover:underline"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default RecipientPicker;
